import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LiveTv as LiveTvIcon,
  PhotoCamera as PhotoCameraIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  type AdminChannelPatchPayload,
  type AdminChannelPlaylistRow,
  type AdminChannelProgramContentRow,
  type AdminChannelProgramRow,
  type AdminChannelRow,
} from "../services/api";
import { getPodcastImageUrl } from "../utils/imageUtils";
import { formatChileanDate } from "../utils/dateUtils";
import { slugify } from "../utils/textUtils";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("No se pudo leer la imagen"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });

const splitCsv = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const joinCsv = (items: string[] | undefined): string => (items ?? []).join(", ");

const localeSort = (a: string, b: string): number =>
  a.localeCompare(b, "es", { sensitivity: "base" });

const sortProgramsByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => localeSort(a.name, b.name));

const sortByTitle = <T extends { title: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => localeSort(a.title, b.title));

const sortByPublishedAtDesc = <T extends { publishedAt?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
    const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
    return dateB - dateA;
  });

const ADMIN_PROGRAM_CONTENT_YEAR = 2026;

const CATEGORY_OPTIONS = [
  { value: "politics", label: "Politics" },
  { value: "other", label: "Other" },
] as const;

const TYPE_OPTIONS = [
  { value: "tv", label: "TV" },
  { value: "noticias", label: "Noticias" },
  { value: "radio", label: "Radio" },
  { value: "digital", label: "Digital" },
] as const;

const getYoutubeContentUrl = (item: AdminChannelProgramContentRow): string | null => {
  if (item.source !== "youtube") return null;
  if (item.url?.trim()) return item.url.trim();
  if (item.sourceId?.trim()) {
    return `https://www.youtube.com/watch?v=${item.sourceId.trim()}`;
  }
  return null;
};

type ChannelDetailDrawerProps = {
  channelId: string | null;
  channelSummary?: AdminChannelRow | null;
  onClose: () => void;
};

type IdentityFormState = {
  title: string;
  slug: string;
  description: string;
};

const emptyIdentityForm = (): IdentityFormState => ({
  title: "",
  slug: "",
  description: "",
});

const identityFormFromChannel = (ch: AdminChannelRow): IdentityFormState => ({
  title: ch.title ?? "",
  slug: ch.slug ?? "",
  description: ch.description ?? "",
});

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "error" in error.response.data &&
    typeof (error.response.data as { error?: unknown }).error === "string"
  ) {
    return (error.response.data as { error: string }).error;
  }
  return fallback;
};

const getLivestreamDisplayLabel = (item: AdminChannelProgramContentRow): string => {
  const parts: string[] = [];
  if (item.livestreamProgramName?.trim()) {
    parts.push(item.livestreamProgramName.trim());
  }
  if (item.livestreamTitle?.trim()) {
    parts.push(item.livestreamTitle.trim());
  }
  return parts.join(" · ");
};

const ChannelDetailDrawer: React.FC<ChannelDetailDrawerProps> = ({
  channelId,
  channelSummary,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const open = Boolean(channelId);

  const [identityForm, setIdentityForm] = useState<IdentityFormState>(emptyIdentityForm());
  const [imageImportUrl, setImageImportUrl] = useState("");
  const [singleProgram, setSingleProgram] = useState(false);
  const [defaultProgramId, setDefaultProgramId] = useState<string>("");
  const [fromProgramId, setFromProgramId] = useState("");
  const [toProgramId, setToProgramId] = useState("");
  const [confirmReassignOpen, setConfirmReassignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminChannelProgramRow | null>(null);
  const [deleteMigrateToProgramId, setDeleteMigrateToProgramId] = useState("");
  const [editTarget, setEditTarget] = useState<AdminChannelProgramRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", slug: "" });
  const [createSlugManual, setCreateSlugManual] = useState(false);
  const [playlistFromId, setPlaylistFromId] = useState("");
  const [playlistToProgramId, setPlaylistToProgramId] = useState("");
  const [playlistCreateProgram, setPlaylistCreateProgram] = useState(false);
  const [playlistProgramForm, setPlaylistProgramForm] = useState({ name: "", slug: "" });
  const [playlistProgramSlugManual, setPlaylistProgramSlugManual] = useState(false);
  const [confirmPlaylistAssignOpen, setConfirmPlaylistAssignOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    slug: "",
    description: "",
    aliases: "",
    keywords: "",
    hashtags: "",
    socialAccounts: "",
    youtubePlaylistIds: "",
    isActive: true,
    hidden: false,
  });
  const [editSlugManual, setEditSlugManual] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [savingContentProgramId, setSavingContentProgramId] = useState<string | null>(
    null,
  );

  const detailQuery = useQuery({
    queryKey: ["admin-channel-detail", channelId],
    queryFn: () => apiService.getAdminChannelDetail(channelId!),
    enabled: open && Boolean(channelId),
  });

  const programsQuery = useQuery({
    queryKey: ["admin-channel-programs", channelId],
    queryFn: () => apiService.getAdminChannelPrograms(channelId!),
    enabled: open && Boolean(channelId),
  });

  const playlistsQuery = useQuery({
    queryKey: ["admin-channel-playlists", channelId],
    queryFn: () => apiService.getAdminChannelPlaylists(channelId!),
    enabled: open && Boolean(channelId),
  });

  const channel = detailQuery.data?.channel ?? channelSummary;
  const programs: AdminChannelProgramRow[] = programsQuery.data?.programs ?? [];
  const catalogPrograms = programsQuery.data?.catalogPrograms ?? programs;
  const playlists: AdminChannelPlaylistRow[] = playlistsQuery.data?.playlists ?? [];
  const playlistsWithContent = useMemo(
    () => playlists.filter((pl) => pl.contentCount > 0),
    [playlists],
  );
  const programsSorted = useMemo(() => sortProgramsByName(programs), [programs]);
  const catalogProgramsSorted = useMemo(
    () => sortProgramsByName(catalogPrograms),
    [catalogPrograms],
  );
  const playlistsWithContentSorted = useMemo(
    () => sortByTitle(playlistsWithContent),
    [playlistsWithContent],
  );
  const programContentYear =
    programsQuery.data?.contentYear ??
    playlistsQuery.data?.contentYear ??
    ADMIN_PROGRAM_CONTENT_YEAR;

  const editProgramContentQuery = useQuery({
    queryKey: ["admin-channel-program-content", channelId, editTarget?._id],
    queryFn: () =>
      apiService.getAdminChannelProgramContent(channelId!, editTarget!._id),
    enabled: open && Boolean(channelId) && Boolean(editTarget),
  });

  const editProgramContentCount =
    editProgramContentQuery.data?.total ??
    editProgramContentQuery.data?.content.length ??
    0;

  const editProgramContentSorted = useMemo(
    () => sortByPublishedAtDesc(editProgramContentQuery.data?.content ?? []),
    [editProgramContentQuery.data?.content],
  );

  useEffect(() => {
    if (!open) {
      setIdentityForm(emptyIdentityForm());
      setImageImportUrl("");
      return;
    }
    const ch = detailQuery.data?.channel ?? channelSummary;
    if (ch) {
      setSingleProgram(ch.singleProgramPodcast === true);
      setDefaultProgramId(ch.defaultProgramId ?? "");
      setIdentityForm(identityFormFromChannel(ch));
    }
  }, [open, detailQuery.data?.channel, channelSummary]);

  useEffect(() => {
    if (!open) {
      setFromProgramId("");
      setToProgramId("");
      setConfirmReassignOpen(false);
      setDeleteTarget(null);
      setDeleteMigrateToProgramId("");
      setEditTarget(null);
      setCreateOpen(false);
      setCreateForm({ name: "", slug: "" });
      setPlaylistFromId("");
      setPlaylistToProgramId("");
      setPlaylistCreateProgram(false);
      setPlaylistProgramForm({ name: "", slug: "" });
      setPlaylistProgramSlugManual(false);
      setConfirmPlaylistAssignOpen(false);
      setSavingContentProgramId(null);
    }
  }, [open]);

  useEffect(() => {
    setDeleteMigrateToProgramId("");
  }, [deleteTarget?._id]);

  useEffect(() => {
    if (!editTarget) return;
    setEditSlugManual(Boolean(editTarget.slug?.trim()));
    setEditForm({
      name: editTarget.name,
      slug: editTarget.slug ?? "",
      description: editTarget.description ?? "",
      aliases: joinCsv(editTarget.aliases),
      keywords: joinCsv(editTarget.keywords),
      hashtags: joinCsv(editTarget.hashtags),
      socialAccounts: joinCsv(editTarget.socialAccounts),
      youtubePlaylistIds: joinCsv(editTarget.youtubePlaylistIds),
      isActive: editTarget.isActive !== false,
      hidden: editTarget.hidden === true,
    });
  }, [editTarget]);

  useEffect(() => {
    if (!playlistFromId) {
      setPlaylistProgramForm({ name: "", slug: "" });
      setPlaylistProgramSlugManual(false);
      return;
    }
    const row = playlistsWithContentSorted.find((pl) => pl.playlistId === playlistFromId);
    if (!row) return;
    setPlaylistProgramForm({
      name: row.title,
      slug: slugify(row.title),
    });
    setPlaylistProgramSlugManual(false);
  }, [playlistFromId, playlistsWithContentSorted]);

  const openCreateDialog = () => {
    setCreateForm({ name: "", slug: "" });
    setCreateSlugManual(false);
    setCreateOpen(true);
  };

  const handleCreateNameChange = (name: string) => {
    setCreateForm((f) => ({
      ...f,
      name,
      slug: createSlugManual ? f.slug : slugify(name),
    }));
  };

  const handleCreateSlugChange = (slug: string) => {
    setCreateSlugManual(true);
    setCreateForm((f) => ({ ...f, slug }));
  };

  const handleEditNameChange = (name: string) => {
    setEditForm((f) => ({
      ...f,
      name,
      slug: editSlugManual ? f.slug : slugify(name),
    }));
  };

  const handleEditSlugChange = (slug: string) => {
    setEditSlugManual(true);
    setEditForm((f) => ({ ...f, slug }));
  };

  const handlePlaylistProgramNameChange = (name: string) => {
    setPlaylistProgramForm((f) => ({
      ...f,
      name,
      slug: playlistProgramSlugManual ? f.slug : slugify(name),
    }));
  };

  const handlePlaylistProgramSlugChange = (slug: string) => {
    setPlaylistProgramSlugManual(true);
    setPlaylistProgramForm((f) => ({ ...f, slug }));
  };

  const settingsMutation = useMutation({
    mutationFn: (payload: AdminChannelPatchPayload) =>
      apiService.patchAdminChannelSettings(channelId!, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(["admin-channel-detail", channelId], result);
      queryClient.invalidateQueries({ queryKey: ["admin-channels"] });
      setSingleProgram(result.channel.singleProgramPodcast === true);
      setDefaultProgramId(result.channel.defaultProgramId ?? "");
      setIdentityForm(identityFormFromChannel(result.channel));
      setSnackbar({
        open: true,
        message: "Configuración del canal guardada",
        severity: "success",
      });
    },
    onError: (error: unknown) => {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, "No se pudo guardar la configuración"),
        severity: "error",
      });
    },
  });

  const identitySaveMutation = useMutation({
    mutationFn: (payload: AdminChannelPatchPayload) =>
      apiService.patchAdminChannelSettings(channelId!, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(["admin-channel-detail", channelId], result);
      void queryClient.invalidateQueries({ queryKey: ["admin-channels"] });
      setIdentityForm(identityFormFromChannel(result.channel));
      setSnackbar({
        open: true,
        message: "Canal actualizado",
        severity: "success",
      });
    },
    onError: (error: unknown) => {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, "No se pudo guardar el canal"),
        severity: "error",
      });
    },
  });

  const applyImageResult = (result: { channel: AdminChannelRow }, message: string) => {
    queryClient.setQueryData(["admin-channel-detail", channelId], result);
    void queryClient.invalidateQueries({ queryKey: ["admin-channels"] });
    setSnackbar({ open: true, message, severity: "success" });
  };

  const imageMutation = useMutation({
    mutationFn: async (file: File) => {
      const imageBase64 = await fileToBase64(file);
      return apiService.uploadAdminChannelImage(channelId!, {
        imageBase64,
        contentType: file.type || "image/png",
        fileName: file.name,
      });
    },
    onSuccess: (result) => applyImageResult(result, "Imagen actualizada"),
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo subir la imagen",
        severity: "error",
      });
    },
  });

  const imageUrlMutation = useMutation({
    mutationFn: (url: string) =>
      apiService.uploadAdminChannelImage(channelId!, { imageUrl: url }),
    onSuccess: (result) => {
      applyImageResult(result, "Imagen importada");
      setImageImportUrl("");
    },
    onError: (error: unknown) => {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, "No se pudo importar la imagen"),
        severity: "error",
      });
    },
  });

  const handleSaveIdentity = () => {
    if (!identityForm.title.trim()) {
      setSnackbar({
        open: true,
        message: "El título es obligatorio",
        severity: "error",
      });
      return;
    }
    identitySaveMutation.mutate({
      title: identityForm.title.trim(),
      slug: identityForm.slug.trim() || undefined,
      description: identityForm.description.trim() || null,
    });
  };

  const handleImportFromUrl = () => {
    const url = imageImportUrl.trim();
    if (!url) {
      setSnackbar({
        open: true,
        message: "Pega una URL de imagen",
        severity: "error",
      });
      return;
    }
    imageUrlMutation.mutate(url);
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackbar({
        open: true,
        message: "El archivo debe ser una imagen",
        severity: "error",
      });
      return;
    }
    imageMutation.mutate(file);
  };

  const reassignMutation = useMutation({
    mutationFn: () =>
      apiService.reassignAdminChannelProgramContent(channelId!, {
        fromProgramId,
        toProgramId,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      setConfirmReassignOpen(false);
      setFromProgramId("");
      setToProgramId("");
      setSnackbar({
        open: true,
        message: `${result.modifiedCount} contenido(s) reasignado(s)`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo reasignar el contenido",
        severity: "error",
      });
    },
  });

  const contentProgramMutation = useMutation({
    mutationFn: ({
      contentId,
      programId,
    }: {
      contentId: string;
      programId: string | null;
    }) => apiService.patchAdminChannelContentProgram(channelId!, contentId, programId),
    onMutate: ({ contentId }) => {
      setSavingContentProgramId(contentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-program-content", channelId, editTarget?._id],
      });
      setSnackbar({
        open: true,
        message: "Programa del contenido actualizado",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo cambiar el programa del contenido",
        severity: "error",
      });
    },
    onSettled: () => {
      setSavingContentProgramId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const name = createForm.name.trim();
      if (!name) throw new Error("El nombre del programa es requerido");
      return apiService.createAdminChannelProgram(channelId!, {
        name,
        slug: createForm.slug.trim() || undefined,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-detail", channelId],
      });
      setCreateOpen(false);
      setCreateForm({ name: "", slug: "" });
      setEditTarget(result.program);
      setSnackbar({
        open: true,
        message: `Programa "${result.program.name}" creado`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo crear el programa",
        severity: "error",
      });
    },
  });

  const playlistAssignMutation = useMutation({
    mutationFn: () => {
      if (!playlistFromId) throw new Error("Selecciona una playlist");
      if (playlistCreateProgram) {
        const name = playlistProgramForm.name.trim();
        if (!name) throw new Error("El nombre del programa es requerido");
        return apiService.assignAdminChannelPlaylistContent(channelId!, {
          playlistId: playlistFromId,
          createProgram: true,
          programName: name,
          programSlug: playlistProgramForm.slug.trim() || undefined,
        });
      }
      if (!playlistToProgramId) throw new Error("Selecciona un programa");
      return apiService.assignAdminChannelPlaylistContent(channelId!, {
        playlistId: playlistFromId,
        toProgramId: playlistToProgramId,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-playlists", channelId],
      });
      setConfirmPlaylistAssignOpen(false);
      setPlaylistFromId("");
      setPlaylistToProgramId("");
      setPlaylistCreateProgram(false);
      setSnackbar({
        open: true,
        message: result.createdProgram
          ? `Programa "${result.programName}" creado — ${result.modifiedCount} contenido(s) asignado(s)`
          : `${result.modifiedCount} contenido(s) asignado(s) a "${result.programName}"`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo asignar el contenido de la playlist",
        severity: "error",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: () => {
      const name = editForm.name.trim();
      if (!name) throw new Error("El nombre del programa es requerido");
      return apiService.patchAdminChannelProgram(channelId!, editTarget!._id, {
        name,
        slug: editForm.slug.trim() || undefined,
        description: editForm.description.trim(),
        aliases: splitCsv(editForm.aliases),
        keywords: splitCsv(editForm.keywords),
        hashtags: splitCsv(editForm.hashtags),
        socialAccounts: splitCsv(editForm.socialAccounts),
        youtubePlaylistIds: splitCsv(editForm.youtubePlaylistIds),
        isActive: editForm.isActive,
        hidden: editForm.hidden,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-detail", channelId],
      });
      setEditTarget(null);
      setSnackbar({
        open: true,
        message: "Programa actualizado",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar el programa",
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (payload: {
      programId: string;
      migrateToProgramId?: string;
      unlinkContent?: boolean;
    }) =>
      apiService.deleteAdminChannelProgram(channelId!, payload.programId, {
        migrateToProgramId: payload.migrateToProgramId,
        unlinkContent: payload.unlinkContent,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-programs", channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-detail", channelId],
      });
      if (defaultProgramId === result.programId) {
        setDefaultProgramId("");
      }
      if (fromProgramId === result.programId) {
        setFromProgramId("");
      }
      if (toProgramId === result.programId) {
        setToProgramId("");
      }
      setDeleteTarget(null);
      setDeleteMigrateToProgramId("");
      let detail = "";
      if (result.migratedContentCount > 0) {
        detail = ` (${result.migratedContentCount} contenido(s) migrado(s))`;
      } else if (result.unlinkedContentCount > 0) {
        detail = ` (${result.unlinkedContentCount} contenido(s) desvinculado(s))`;
      }
      setSnackbar({
        open: true,
        message: `Programa eliminado${detail}`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo eliminar el programa",
        severity: "error",
      });
    },
  });

  const fromProgram = programsSorted.find((p) => p._id === fromProgramId);
  const selectedPlaylist = playlistsWithContentSorted.find(
    (pl) => pl.playlistId === playlistFromId,
  );
  const playlistTargetProgram = playlistCreateProgram
    ? null
    : catalogProgramsSorted.find((p) => p._id === playlistToProgramId) ??
      programsSorted.find((p) => p._id === playlistToProgramId);
  const canReassign =
    fromProgramId &&
    toProgramId &&
    fromProgramId !== toProgramId &&
    (fromProgram?.contentCount ?? 0) > 0;

  const canAssignPlaylist = playlistCreateProgram
    ? Boolean(playlistFromId && playlistProgramForm.name.trim())
    : Boolean(playlistFromId && playlistToProgramId);

  const deleteMigrationTargets = useMemo(
    () =>
      sortProgramsByName(catalogPrograms.filter((p) => p._id !== deleteTarget?._id)),
    [catalogPrograms, deleteTarget?._id],
  );

  const reassignToPrograms = useMemo(() => {
    const seen = new Set<string>();
    const merged: { _id: string; name: string; contentCount?: number }[] = [];
    for (const prog of programsSorted) {
      if (prog._id === fromProgramId || seen.has(prog._id)) continue;
      seen.add(prog._id);
      merged.push({ _id: prog._id, name: prog.name, contentCount: prog.contentCount });
    }
    for (const prog of catalogProgramsSorted) {
      if (prog._id === fromProgramId || seen.has(prog._id)) continue;
      seen.add(prog._id);
      merged.push({ _id: prog._id, name: prog.name });
    }
    return sortProgramsByName(merged);
  }, [programsSorted, catalogProgramsSorted, fromProgramId]);

  const deleteTargetHasContent = (deleteTarget?.contentCount ?? 0) > 0;
  const canDeleteWithMigration =
    deleteTarget &&
    deleteTargetHasContent &&
    deleteMigrationTargets.length > 0 &&
    Boolean(deleteMigrateToProgramId);
  const canDeleteWithUnlink =
    deleteTarget && deleteTargetHasContent && deleteMigrationTargets.length === 0;
  const canDeleteEmpty = deleteTarget && !deleteTargetHasContent;

  const handleSingleProgramToggle = (checked: boolean) => {
    setSingleProgram(checked);
    settingsMutation.mutate({
      singleProgramPodcast: checked,
    });
  };

  const handleDefaultProgramChange = (programId: string) => {
    setDefaultProgramId(programId);
    settingsMutation.mutate({
      defaultProgramId: programId || null,
    });
  };

  const copyContentId = async (contentId: string) => {
    try {
      await navigator.clipboard.writeText(contentId);
      setSnackbar({
        open: true,
        message: "ID del contenido copiado",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "No se pudo copiar el ID",
        severity: "error",
      });
    }
  };

  const copyLivestreamStreamId = async (streamId: string) => {
    try {
      await navigator.clipboard.writeText(streamId);
      setSnackbar({
        open: true,
        message: "ID de livestream_streams copiado",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "No se pudo copiar el ID del livestream",
        severity: "error",
      });
    }
  };

  const coverUrl = channel ? getPodcastImageUrl(channel) : undefined;
  const imageBusy = imageMutation.isPending || imageUrlMutation.isPending;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            bgcolor: "background.default",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={coverUrl}
                alt={channel?.title ?? ""}
                sx={{ width: 56, height: 56 }}
              >
                {channel?.title?.charAt(0) ?? "?"}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageBusy}
                sx={{
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                {imageBusy ? (
                  <CircularProgress size={16} />
                ) : (
                  <PhotoCameraIcon fontSize="small" />
                )}
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={600} noWrap>
                {channel?.title ?? "Canal"}
              </Typography>
              {channel?.slug && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {channel.slug}
                </Typography>
              )}
              {channel && (
                <Typography variant="caption" color="text.secondary">
                  {channel.category} · {channel.type}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5, overflow: "auto", flex: 1 }}>
          {detailQuery.isLoading && !channelSummary ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : detailQuery.isError ? (
            <Alert severity="error">No se pudo cargar el detalle del canal.</Alert>
          ) : (
            <Stack spacing={2.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Imagen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Se guarda como PNG 800×800.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="URL de imagen"
                  size="small"
                  fullWidth
                  placeholder="https://…"
                  value={imageImportUrl}
                  onChange={(e) => setImageImportUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleImportFromUrl();
                    }
                  }}
                  disabled={imageUrlMutation.isPending}
                />
                <Button
                  variant="outlined"
                  onClick={handleImportFromUrl}
                  disabled={imageUrlMutation.isPending || !imageImportUrl.trim()}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {imageUrlMutation.isPending ? "Importando…" : "Importar URL"}
                </Button>
              </Stack>

              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Identidad
              </Typography>
              <TextField
                label="Título"
                size="small"
                fullWidth
                value={identityForm.title}
                onChange={(e) =>
                  setIdentityForm((prev) => ({ ...prev, title: e.target.value }))
                }
              />
              <TextField
                label="Slug"
                size="small"
                fullWidth
                value={identityForm.slug}
                onChange={(e) =>
                  setIdentityForm((prev) => ({ ...prev, slug: e.target.value }))
                }
              />
              <TextField
                label="Descripción"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={identityForm.description}
                onChange={(e) =>
                  setIdentityForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
              <Button
                variant="contained"
                onClick={handleSaveIdentity}
                disabled={identitySaveMutation.isPending}
              >
                {identitySaveMutation.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>

              <Divider />

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  Clasificación
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      value={channel?.category ?? ""}
                      label="Categoría"
                      disabled={!channel || settingsMutation.isPending}
                      onChange={(e) => {
                        const category = e.target.value as AdminChannelRow["category"];
                        if (category === channel?.category) return;
                        settingsMutation.mutate({ category });
                      }}
                    >
                      {CATEGORY_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={channel?.type ?? ""}
                      label="Tipo"
                      disabled={!channel || settingsMutation.isPending}
                      onChange={(e) => {
                        const type = e.target.value as AdminChannelRow["type"];
                        if (type === channel?.type) return;
                        settingsMutation.mutate({ type });
                      }}
                    >
                      {TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  Un solo programa
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={singleProgram}
                      disabled={settingsMutation.isPending}
                      onChange={(e) => handleSingleProgramToggle(e.target.checked)}
                    />
                  }
                  label="Canal de un solo programa (singleProgramPodcast)"
                />
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Programa por defecto</InputLabel>
                  <Select
                    value={defaultProgramId}
                    label="Programa por defecto"
                    onChange={(e) => handleDefaultProgramChange(e.target.value)}
                    disabled={settingsMutation.isPending}
                  >
                    <MenuItem value="">
                      <em>Auto (nombre del canal)</em>
                    </MenuItem>
                    {catalogProgramsSorted.map((prog) => (
                      <MenuItem key={prog._id} value={prog._id}>
                        {prog.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Programa usado como fallback en clasificación. No requiere activar &quot;un solo
                  programa&quot;.
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Programas
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Conteo de {programContentYear}: videos de más de 5 minutos.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    disabled={createMutation.isPending}
                  >
                    Crear
                  </Button>
                </Stack>
                {programsQuery.isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : programs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No hay programas en este canal. Crea uno para empezar.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Programa</TableCell>
                          <TableCell align="right">Contenido {programContentYear}</TableCell>
                          <TableCell align="right" width={96} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {programsSorted.map((prog) => (
                          <TableRow key={prog._id}>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2">{prog.name}</Typography>
                                {(!prog.isActive || prog.hidden) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {[
                                      !prog.isActive ? "inactivo" : null,
                                      prog.hidden ? "oculto" : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{prog.contentCount}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar programa">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={editMutation.isPending || deleteMutation.isPending}
                                    onClick={() => setEditTarget(prog)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Eliminar programa">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={editMutation.isPending || deleteMutation.isPending}
                                    onClick={() => setDeleteTarget(prog)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Playlists
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Playlists de YouTube sincronizadas. Conteo de {programContentYear}: videos de
                  más de 5 minutos.
                </Typography>
                {playlistsQuery.isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : playlistsWithContent.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {playlists.length === 0
                      ? "No hay playlists sincronizadas. Ejecuta "
                      : "Ninguna playlist tiene contenido en " +
                        programContentYear +
                        ". Ejecuta "}
                    <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                      migrate:sync-channel-playlists
                    </Typography>{" "}
                    {playlists.length === 0 ? "para poblar el catálogo." : "si aún no lo hiciste."}
                  </Typography>
                ) : (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Playlist</TableCell>
                          <TableCell align="right">Contenido {programContentYear}</TableCell>
                          <TableCell>Programa</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {playlistsWithContentSorted.map((pl) => (
                          <TableRow key={pl._id}>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                  {pl.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: "monospace" }}
                                >
                                  {pl.playlistId}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{pl.contentCount}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {pl.programName ?? "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Asignar playlist a programa
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mueve todo el contenido de {programContentYear} de una playlist al programa
                  seleccionado, o crea un programa nuevo desde la playlist.
                </Typography>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Playlist</InputLabel>
                    <Select
                      value={playlistFromId}
                      label="Playlist"
                      onChange={(e) => setPlaylistFromId(e.target.value)}
                      disabled={playlistsWithContent.length === 0 || playlistAssignMutation.isPending}
                    >
                      <MenuItem value="">
                        <em>Seleccionar…</em>
                      </MenuItem>
                      {playlistsWithContentSorted.map((pl) => (
                        <MenuItem key={pl.playlistId} value={pl.playlistId}>
                          {pl.title} ({pl.contentCount})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={playlistCreateProgram}
                        onChange={(e) => {
                          setPlaylistCreateProgram(e.target.checked);
                          if (e.target.checked) {
                            setPlaylistToProgramId("");
                          }
                        }}
                        disabled={!playlistFromId || playlistAssignMutation.isPending}
                      />
                    }
                    label="Crear programa desde la playlist"
                  />

                  {playlistCreateProgram ? (
                    <>
                      <TextField
                        label="Nombre del programa"
                        value={playlistProgramForm.name}
                        onChange={(e) => handlePlaylistProgramNameChange(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={!playlistFromId || playlistAssignMutation.isPending}
                      />
                      <TextField
                        label="Slug"
                        value={playlistProgramForm.slug}
                        onChange={(e) => handlePlaylistProgramSlugChange(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={!playlistFromId || playlistAssignMutation.isPending}
                        helperText="Se genera automáticamente desde el nombre. Puedes editarlo manualmente."
                      />
                    </>
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>Programa destino</InputLabel>
                      <Select
                        value={playlistToProgramId}
                        label="Programa destino"
                        onChange={(e) => setPlaylistToProgramId(e.target.value)}
                        disabled={!playlistFromId || playlistAssignMutation.isPending}
                      >
                        <MenuItem value="">
                          <em>Seleccionar…</em>
                        </MenuItem>
                        {catalogProgramsSorted.map((prog) => (
                          <MenuItem key={prog._id} value={prog._id}>
                            {prog.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Button
                    variant="contained"
                    disabled={!canAssignPlaylist || playlistAssignMutation.isPending}
                    onClick={() => setConfirmPlaylistAssignOpen(true)}
                  >
                    Asignar contenido de la playlist
                  </Button>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  Reasignar contenido
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mueve todo el contenido de {programContentYear} de un programa a otro dentro de
                  este canal.
                </Typography>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Desde programa</InputLabel>
                    <Select
                      value={fromProgramId}
                      label="Desde programa"
                      onChange={(e) => setFromProgramId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Seleccionar…</em>
                      </MenuItem>
                      {programsSorted.map((prog) => (
                        <MenuItem key={prog._id} value={prog._id}>
                          {prog.name} ({prog.contentCount})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Hacia programa</InputLabel>
                    <Select
                      value={toProgramId}
                      label="Hacia programa"
                      onChange={(e) => setToProgramId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Seleccionar…</em>
                      </MenuItem>
                      {reassignToPrograms.map((prog) => (
                        <MenuItem key={prog._id} value={prog._id}>
                          {prog.name}
                          {prog.contentCount != null ? ` (${prog.contentCount})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    disabled={!canReassign || reassignMutation.isPending}
                    onClick={() => setConfirmReassignOpen(true)}
                  >
                    Mover todo el contenido
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Crear programa</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Nombre"
              value={createForm.name}
              onChange={(e) => handleCreateNameChange(e.target.value)}
              fullWidth
              required
              size="small"
              autoFocus
            />
            <TextField
              label="Slug"
              value={createForm.slug}
              onChange={(e) => handleCreateSlugChange(e.target.value)}
              fullWidth
              size="small"
              helperText="Se genera automáticamente desde el nombre. Puedes editarlo manualmente."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !createForm.name.trim()}
          >
            {createMutation.isPending ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReassignOpen} onClose={() => setConfirmReassignOpen(false)}>
        <DialogTitle>Confirmar reasignación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se moverán {fromProgram?.contentCount ?? 0} contenido(s) de {programContentYear} de{" "}
            <strong>{fromProgram?.name}</strong> al programa seleccionado. Esta acción no se
            puede deshacer fácilmente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReassignOpen(false)} disabled={reassignMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => reassignMutation.mutate()}
            disabled={reassignMutation.isPending}
          >
            {reassignMutation.isPending ? "Moviendo…" : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmPlaylistAssignOpen}
        onClose={() => !playlistAssignMutation.isPending && setConfirmPlaylistAssignOpen(false)}
      >
        <DialogTitle>Confirmar asignación de playlist</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se asignarán {selectedPlaylist?.contentCount ?? 0} contenido(s) de{" "}
            {programContentYear} de la playlist <strong>{selectedPlaylist?.title}</strong>
            {playlistCreateProgram ? (
              <>
                {" "}
                al nuevo programa <strong>{playlistProgramForm.name.trim()}</strong>.
              </>
            ) : (
              <>
                {" "}
                al programa <strong>{playlistTargetProgram?.name}</strong>.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmPlaylistAssignOpen(false)}
            disabled={playlistAssignMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => playlistAssignMutation.mutate()}
            disabled={playlistAssignMutation.isPending}
          >
            {playlistAssignMutation.isPending ? "Asignando…" : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={() => !editMutation.isPending && setEditTarget(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Editar programa</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ pt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <TextField
                  label="Nombre"
                  value={editForm.name}
                  onChange={(e) => handleEditNameChange(e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Slug"
                  value={editForm.slug}
                  onChange={(e) => handleEditSlugChange(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Se genera automáticamente desde el nombre. Puedes editarlo manualmente."
                />
                <TextField
                  label="Descripción"
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
                <Divider />
                <TextField
                  label="Aliases (separados por coma)"
                  value={editForm.aliases}
                  onChange={(e) => setEditForm((f) => ({ ...f, aliases: e.target.value }))}
                  fullWidth
                  size="small"
                  helperText="Nombres alternativos usados en clasificación de programas"
                />
                <TextField
                  label="Keywords (separadas por coma)"
                  value={editForm.keywords}
                  onChange={(e) => setEditForm((f) => ({ ...f, keywords: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Hashtags sin # (separados por coma)"
                  value={editForm.hashtags}
                  onChange={(e) => setEditForm((f) => ({ ...f, hashtags: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Cuentas sociales sin @ (separadas por coma)"
                  value={editForm.socialAccounts}
                  onChange={(e) => setEditForm((f) => ({ ...f, socialAccounts: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="YouTube playlist IDs (separados por coma)"
                  value={editForm.youtubePlaylistIds}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, youtubePlaylistIds: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                  }
                  label="Activo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.hidden}
                      onChange={(e) => setEditForm((f) => ({ ...f, hidden: e.target.checked }))}
                    />
                  }
                  label="Oculto (excluido de rankings y explorer)"
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1.5} sx={{ height: "100%" }}>
                <Typography variant="subtitle2">
                  Contenido {programContentYear}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {editProgramContentQuery.isLoading
                    ? "Cargando contenido…"
                    : `${editProgramContentCount} contenido(s) asignado(s) a este programa (video, >5 min)`}
                </Typography>
                {editTarget ? (
                  editProgramContentQuery.isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : editProgramContentQuery.isError ? (
                    <Typography variant="body2" color="error">
                      No se pudieron cargar los contenidos
                    </Typography>
                  ) : (editProgramContentQuery.data?.content.length ?? 0) > 0 ? (
                    <Box
                      sx={{
                        flex: 1,
                        maxHeight: "min(70vh, 640px)",
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Stack divider={<Divider flexItem />} spacing={1.5}>
                        {editProgramContentSorted.map((item) => {
                          const isSaving = savingContentProgramId === item._id;
                          const youtubeUrl = getYoutubeContentUrl(item);
                          const livestreamLabel = getLivestreamDisplayLabel(item);
                          return (
                            <Stack key={item._id} spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ wordBreak: "break-word" }}
                                  >
                                    {item.title}
                                  </Typography>
                                  {item.playlistNames && item.playlistNames.length > 0 ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ wordBreak: "break-word" }}
                                    >
                                      {item.playlistNames.join(" · ")}
                                    </Typography>
                                  ) : null}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {item.publishedAt
                                      ? formatChileanDate(item.publishedAt)
                                      : "Sin fecha"}
                                  </Typography>
                                  {item.source === "youtube" ? (
                                    item.livestreamStreamId ? (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        sx={{ mt: 0.25 }}
                                      >
                                        <LiveTvIcon
                                          sx={{ fontSize: 14, color: "success.main" }}
                                        />
                                        <Typography
                                          variant="caption"
                                          color="success.main"
                                          sx={{ wordBreak: "break-word", flex: 1 }}
                                        >
                                          Livestream
                                          {livestreamLabel ? `: ${livestreamLabel}` : ""}
                                        </Typography>
                                        <Tooltip title="Copiar ID de livestream_streams">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              void copyLivestreamStreamId(item.livestreamStreamId!)
                                            }
                                            aria-label="Copiar ID de livestream_streams"
                                            sx={{ p: 0.25, color: "text.secondary" }}
                                          >
                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        display="block"
                                        sx={{ mt: 0.25 }}
                                      >
                                        Sin livestream_streams
                                      </Typography>
                                    )
                                  ) : null}
                                  {item.description?.trim() ? (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        mt: 0.5,
                                        wordBreak: "break-word",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {item.description}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ mt: 0.5, fontStyle: "italic" }}
                                    >
                                      Sin descripción
                                    </Typography>
                                  )}
                                </Box>
                                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                                  <Tooltip title="Copiar ID del contenido">
                                    <IconButton
                                      size="small"
                                      onClick={() => void copyContentId(item._id)}
                                      aria-label="Copiar ID del contenido"
                                      sx={{ mt: -0.25, color: "text.secondary" }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {youtubeUrl ? (
                                    <Tooltip title="Abrir en YouTube">
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={youtubeUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ mt: -0.25 }}
                                      >
                                        <YouTubeIcon fontSize="small" color="error" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              </Stack>
                              <FormControl fullWidth size="small">
                                <InputLabel>Programa</InputLabel>
                                <Select
                                  value={editTarget._id}
                                  label="Programa"
                                  disabled={
                                    isSaving ||
                                    contentProgramMutation.isPending ||
                                    editMutation.isPending
                                  }
                                  onChange={(e) => {
                                    const nextProgramId = e.target.value;
                                    if (nextProgramId === editTarget._id) return;
                                    contentProgramMutation.mutate({
                                      contentId: item._id,
                                      programId: nextProgramId || null,
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Sin programa</em>
                                  </MenuItem>
                                  {catalogProgramsSorted.map((prog) => (
                                    <MenuItem key={prog._id} value={prog._id}>
                                      {prog.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No hay videos de más de 5 minutos asignados a este programa en{" "}
                      {programContentYear}
                    </Typography>
                  )
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={editMutation.isPending || !editForm.name.trim()}
          >
            {editMutation.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteMutation.isPending && setDeleteTarget(null)}
      >
        <DialogTitle>Eliminar programa</DialogTitle>
        <DialogContent>
          {deleteTargetHasContent ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Alert severity="warning">
                <strong>{deleteTarget?.name}</strong> tiene {deleteTarget?.contentCount}{" "}
                contenido(s) de {programContentYear} asignado(s).
                {deleteMigrationTargets.length > 0
                  ? " Selecciona otro programa para migrarlos antes de eliminar."
                  : " No hay otros programas en este canal; al eliminar, ese contenido quedará sin programa asignado."}
              </Alert>
              {deleteMigrationTargets.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Migrar contenido a</InputLabel>
                  <Select
                    value={deleteMigrateToProgramId}
                    label="Migrar contenido a"
                    onChange={(e) => setDeleteMigrateToProgramId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Seleccionar…</em>
                    </MenuItem>
                    {deleteMigrationTargets.map((prog) => (
                      <MenuItem key={prog._id} value={prog._id}>
                        {prog.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          ) : (
            <DialogContentText>
              ¿Eliminar <strong>{deleteTarget?.name}</strong>? Este programa no tiene contenido
              asignado.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancelar
          </Button>
          {canDeleteWithMigration && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                  migrateToProgramId: deleteMigrateToProgramId,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando…" : "Migrar y eliminar"}
            </Button>
          )}
          {canDeleteWithUnlink && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                  unlinkContent: true,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando…" : "Eliminar y desvincular"}
            </Button>
          )}
          {canDeleteEmpty && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </>
  );
};

export default ChannelDetailDrawer;
